"use client";

import React from "react";
import Image from "next/image";

/** 右侧配图：注塑件展示，可替换为实际路径 */
const DEFAULT_IMAGE = "/image/new-version/molding/injection-molding/what-is-injection-molding.webp";

/**
 * What is Injection Molding 区块：顶部两列（左文右图），底部优势/劣势双卡片
 * 文案按需求固定为英文，与设计图一致
 */
export default function InjectionMoldingWhatIs({ imageSrc = DEFAULT_IMAGE }: { imageSrc?: string }) {
	return (
		<section className="w-full bg-white px-4 py-8 sm:px-6 sm:py-10 md:py-12 lg:py-16">
			<div className="container mx-auto max-w-6xl">
				{/* 左上角绿色小图标 */}
				<div className="mb-6 flex items-center">
					<span className="align-bottom font-bold leading-[0] text-[#66bb6a] lg:text-[100px]">* </span>
				</div>

				{/* 上部分：两列布局 - 左文右图 */}
				<div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12">
					{/* 左列：标题 + 引言 + 三个要点 */}
					<div className="min-w-0 space-y-6">
						<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							What is Injection Molding?
						</h2>
						<p className="text-sm leading-relaxed text-black md:text-base">
							Injection molding is a high-precision, high-volume manufacturing process for producing identical
							plastic or metal components. The following three points outline its core principles, economic
							rationale, and key advantages.
						</p>

						<div className="space-y-6">
							<div>
								<h3 className="mb-2 text-base font-semibold text-black sm:text-lg">
									1. Core Process: From Material to Final Part
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">
									The process involves injecting molten plastic or metal feedstock into a precision-engineered
									mold cavity under high pressure. The material is then rapidly cooled and solidified, after
									which the finished part is ejected. This cycle is highly automated and repeatable, making it
									the foremost method for mass-producing complex, net-shape plastic components with
									exceptional dimensional accuracy.
								</p>
							</div>
							<div>
								<h3 className="mb-2 text-base font-semibold text-black sm:text-lg">
									2. Economic Advantage: Scale Drives Value
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">
									Compared to prototyping methods like CNC machining or 3D printing, injection molding
									requires a significant initial investment in custom mold tooling. This cost is efficiently
									distributed across high-volume production runs, resulting in the lowest per-part cost at
									scale. This makes it the most cost-effective solution for commercial and industrial volume
									manufacturing.
								</p>
							</div>
							<div>
								<h3 className="mb-2 text-base font-semibold text-black sm:text-lg">
									3. Material Versatility & Production Consistency
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">
									The process supports an extensive range of engineering-grade materials, including
									thermoplastics, thermosets, and Liquid Silicone Rubber (LSR). Materials can be compounded
									with additives to meet specific performance requirements. Crucially, by utilizing the same
									precision mold for an entire production lot, injection molding guarantees high consistency,
									repeatability, and reliable part quality across thousands or millions of units.
								</p>
							</div>
						</div>
					</div>

					{/* 右列：浅灰底图，占满宽度、上下居中 */}
					<div className="flex w-full items-center">
						<div className="relative flex min-h-[280px] w-full items-center justify-center overflow-hidden rounded-lg bg-[#f0f0f0] md:min-h-[320px]">
							<Image
								src={imageSrc}
								alt="Injection molded plastic part"
								width={600}
								height={400}
								className="h-auto w-full object-contain object-center"
							/>
						</div>
					</div>
				</div>

				{/* 下部分：优势 / 劣势 双卡片 */}
				<div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-12">
					{/* Advantages 卡片 */}
					<div className="rounded-lg bg-white p-6 shadow-card md:p-8">
						<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:text-2xl">
							Advantages of Injection Molding
						</h3>
						<ul className="list-none space-y-2 pl-0">
							{[
								"High-volume manufacturing with exceptional consistency",
								"Low per-unit cost at scale after setup",
								"Compatibility with engineering materials and LSR",
								"High precision for complex part designs",
							].map((item, i) => (
								<li
									key={i}
									className="flex items-start gap-2 text-sm leading-relaxed text-black md:text-base"
								>
									<span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#66bb6a]" />
									<span>{item}</span>
								</li>
							))}
						</ul>
					</div>

					{/* Disadvantages 卡片 */}
					<div className="rounded-lg bg-white p-6 shadow-card md:p-8">
						<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:text-2xl">
							Disadvantages of Injection Molding
						</h3>
						<ul className="list-none space-y-2 pl-0">
							{[
								"Significant initial tooling investment required",
								"Longer lead times for mold fabrication",
								"Design for manufacturability constraints apply",
								"Not cost-effective for low-volume production",
							].map((item, i) => (
								<li
									key={i}
									className="flex items-start gap-2 text-sm leading-relaxed text-black md:text-base"
								>
									<span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#66bb6a]" />
									<span>{item}</span>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>
		</section>
	);
}
